import { canPlayOnArm, legalArmIndices } from './Chain.js';
import { tileEquals, type Chain, type Tile } from './types.js';

/** A legal play candidate: a tile from the player's hand on a specific arm. */
export interface PlayCandidate {
  readonly tile: Tile;
  readonly armIndex: number;
}

/**
 * Enumerate every legal (tile, arm) pair for `hand` against `chain`. Pre-lead
 * (empty chain), every tile in hand is a legal lead with `armIndex = -1` (the
 * caller passes -1 to `applyLead` instead of `applyPlay`).
 *
 * `forcedLeadTile`, when set on an empty chain (Телефон first-hand rule),
 * restricts the candidate list to just that tile (when it's in `hand`).
 */
export function enumeratePlays(
  chain: Chain,
  hand: readonly Tile[],
  forcedLeadTile: Tile | null = null,
): PlayCandidate[] {
  if (chain.lead === null) {
    if (forcedLeadTile !== null) {
      return hand
        .filter((t) => tileEquals(t, forcedLeadTile))
        .map((tile) => ({ tile, armIndex: -1 }));
    }
    return hand.map((tile) => ({ tile, armIndex: -1 }));
  }
  const candidates: PlayCandidate[] = [];
  const armIndices = legalArmIndices(chain);
  for (const tile of hand) {
    for (const armIndex of armIndices) {
      if (canPlayOnArm(chain, armIndex, tile)) {
        candidates.push({ tile, armIndex });
      }
    }
  }
  return candidates;
}

/** True if `hand` contains any legal play against `chain`. */
export function hasLegalPlay(
  chain: Chain,
  hand: readonly Tile[],
  forcedLeadTile: Tile | null = null,
): boolean {
  if (chain.lead === null) {
    if (forcedLeadTile !== null) {
      return hand.some((t) => tileEquals(t, forcedLeadTile));
    }
    return hand.length > 0;
  }
  const armIndices = legalArmIndices(chain);
  for (const tile of hand) {
    for (const armIndex of armIndices) {
      if (canPlayOnArm(chain, armIndex, tile)) return true;
    }
  }
  return false;
}
